import { useMemo } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAdminPageFilters } from '@/hooks/useAdminPageFilters';
import {
  boundsForTimeRange,
  daysForTimeRange,
  labelForTimeRange,
  shouldShowPriorComparison,
} from '@/lib/timeRangeOptions';
import { useModerationDashboard } from '../api';
import { ModerationDashboardFilters } from '../components/ModerationDashboardFilters';
import { ModerationPipelineFunnel } from '../components/ModerationPipelineFunnel';
import { ModerationSummaryKpis, type ModerationKpiView } from '../components/ModerationSummaryKpis';
import { PenaltyManagementFigmaCard } from '../components/PenaltyManagementFigmaCard';
import { PlatformSafetyHealthCard } from '../components/PlatformSafetyHealthCard';
import { RecentFlaggedCard, TopOffendersCard } from '../components/Phase4Cards';
import { ReportsOverviewFigmaCard } from '../components/ReportsOverviewFigmaCard';
import type { ModerationMetricDTO } from '../dashboardTypes';
import {
  buildModerationDashboardCsv,
  csvInputFromDashboard,
  downloadTextFile,
  moderationCsvFilename,
} from '../lib/exportModerationCsv';

function num(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function toKpiView(
  metric: ModerationMetricDTO,
  showPriorComparison: boolean,
): ModerationKpiView {
  const view: ModerationKpiView = {
    label: metric.label ?? metric.key ?? 'Metric',
    value: num(metric.value),
    delta: showPriorComparison ? num(metric.deltaPercent) : null,
  };
  if (metric.key) view.key = metric.key;
  if (metric.description) view.description = metric.description;
  if (metric.availability) view.availability = metric.availability;
  if (metric.note) view.note = metric.note;
  return view;
}

function priorWindowLabel(startDate: string | undefined, days: number | null): string | undefined {
  if (!startDate || days == null) return undefined;
  try {
    const start = parseISO(startDate);
    return `${format(subDays(start, days), 'MMM d')} - ${format(subDays(start, 1), 'MMM d')}`;
  } catch {
    return undefined;
  }
}

/**
 * Admin Moderation Dashboard (TM-3450) — Figma layout:
 * left stack (top KPIs / Safety / mid KPIs / Reports / Penalties)
 * + right rail (Pipeline / Offenders / Flagged).
 */
export function ModerationDashboardPage() {
  const {
    range,
    setRange,
    countryCode: locationCode,
    setCountryCode: setLocationCode,
  } = useAdminPageFilters();
  const bounds = boundsForTimeRange(range);
  const globalDays = daysForTimeRange(range);
  const showPriorComparison = shouldShowPriorComparison(range);

  const overviewQuery = useModerationDashboard(bounds, locationCode || undefined);
  const overviewBusy =
    !overviewQuery.isError && (overviewQuery.isPending || overviewQuery.isPlaceholderData);
  const data = overviewQuery.data;

  const kpis = useMemo(
    () => (data?.summaryKpis ?? []).map((metric) => toKpiView(metric, showPriorComparison)),
    [data?.summaryKpis, showPriorComparison],
  );

  const comparisonLabel = showPriorComparison
    ? priorWindowLabel(
        data?.startDate == null ? undefined : String(data.startDate),
        globalDays,
      )
    : undefined;

  function handleExport() {
    if (!data) return;
    const csvBody = buildModerationDashboardCsv(
      csvInputFromDashboard(data, kpis, labelForTimeRange(range), globalDays, locationCode),
    );
    downloadTextFile(moderationCsvFilename(globalDays), csvBody);
    toast.success('Moderation dashboard CSV downloaded.');
  }

  if (overviewQuery.isLoading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (overviewQuery.isError && !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <AlertCircle className="size-8 text-rose-400" />
        <p className="text-sm text-muted-foreground">Failed to load moderation dashboard.</p>
        <Button type="button" variant="outline" onClick={() => void overviewQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const startDate = data?.startDate == null ? undefined : String(data.startDate);
  const endDate = data?.endDate == null ? undefined : String(data.endDate);
  const kpiProps = {
    metrics: kpis,
    isLoading: overviewBusy,
    ...(!overviewBusy && comparisonLabel ? { comparisonLabel } : {}),
  };
  const systemHealthMetric = data?.summaryKpis?.find((m) => m.key === 'SYSTEM_HEALTH');

  return (
    <div className="space-y-4 p-4 md:p-6">
      <ModerationDashboardFilters
        range={range}
        onRangeChange={setRange}
        {...(!overviewBusy && startDate ? { startDate } : {})}
        {...(!overviewBusy && endDate ? { endDate } : {})}
        onExportCsv={handleExport}
        exportDisabled={!data || overviewBusy}
        advancedExportComingSoon={data?.advancedExportAvailability === 'COMING_SOON'}
      />

      {overviewQuery.isError && data ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          <AlertCircle className="size-4 shrink-0" />
          <span className="flex-1">Could not refresh — showing last loaded data.</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 border-rose-400/40"
            onClick={() => void overviewQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2.15fr)_minmax(310px,1fr)]">
        <div className="space-y-4">
          <ModerationSummaryKpis {...kpiProps} group="top" className="sm:grid-cols-3" />

          <PlatformSafetyHealthCard
            module={data?.platformSafetyHealth}
            {...(systemHealthMetric ? { systemHealth: systemHealthMetric } : {})}
            isLoading={overviewBusy}
            showPriorComparison={showPriorComparison}
            {...(!overviewBusy && comparisonLabel ? { comparisonLabel } : {})}
          />

          <ModerationSummaryKpis {...kpiProps} group="secondary" className="sm:grid-cols-3" />

          <ReportsOverviewFigmaCard
            module={data?.reportsOverview}
            reportTypes={data ? (data.reportTypes ?? []) : undefined}
            categories={data ? (data.reportCategoryCounts ?? []) : undefined}
            isLoading={overviewBusy}
            range={range}
            onRangeChange={setRange}
            locationCode={locationCode}
            onLocationCodeChange={setLocationCode}
            availableCountryCodes={data?.availableCountryCodes ?? []}
            {...(!overviewBusy && startDate ? { startDate } : {})}
            {...(!overviewBusy && endDate ? { endDate } : {})}
          />

          <PenaltyManagementFigmaCard
            module={data?.penaltyManagement}
            penaltyTypes={data ? (data.penaltyTypeCounts ?? []) : undefined}
            isLoading={overviewBusy}
          />
        </div>

        <div className="space-y-4">
          <ModerationPipelineFunnel
            module={data?.moderationPipeline}
            isLoading={overviewBusy}
          />
          <TopOffendersCard
            module={data?.topOffendingUsers}
            offenders={data ? (data.topOffenders ?? []) : undefined}
            isLoading={overviewBusy}
          />
          <RecentFlaggedCard
            module={data?.recentContentFlagged}
            items={data ? (data.recentFlagged ?? []) : undefined}
            isLoading={overviewBusy}
          />
          {data?.dataSourceNote ? (
            <p className="text-[11px] leading-4 text-muted-foreground">{data.dataSourceNote}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
